/**
 * CDB-TQS API — Personal Files versioning (Drive 2.0 version history)
 *
 * Talks to the cdb_tqs `/personal-files` router via the BFF proxy base
 * (`CDB_TQS_API_BASE_URL` already resolves to `.../api/v1/tqs`). Every call
 * carries the user Bearer token; the backend owner-guard authorizes reads and
 * writes (BOLA-safe), not the frontend.
 */

import { logApiCall, logApiResponse, logError } from "@/lib/logger";
import {
  CDB_TQS_API_BASE_URL,
  CdbTqsApiError,
  fetchWithLogging,
  getAuthHeaders,
  getMimeTypeFromFilename,
} from "./http";

/** Result of `POST /personal-files/{id}/export/pdf` (#1214). */
export interface PersonalFilePdfExport {
  /** The stored PDF — a new chain, or a new version of the existing one. */
  file: PersonalFileListItem;
  /**
   * Constructs the conversion could not carry over: `"script"` and/or
   * `"canvas"`. Empty when the page is static.
   */
  warnings: string[];
}

export interface PersonalFileVersion {
  id: string;
  version_number: number;
  created_at: string | null;
  size_bytes: number;
  mime_type: string;
}

/**
 * A latest-of-chain row from `GET /personal-files` — the versioned store that
 * holds versioned files and their history — everything the assistant generated,
 * plus every editable UPLOAD, which is mirrored into a chain so the editors can
 * save into it. `source` hints origin: `ai` (generated/edited by the assistant),
 * `user`, or `upload`.
 */
export interface PersonalFileListItem {
  id: string;
  filename: string;
  mime_type: string;
  size_bytes: number;
  version_number: number;
  source?: string;
  created_at: string | null;
  /** ISO-8601 timestamp — set once personal_file_indexer processed this
   *  version. null = not indexed yet. */
  indexed_at?: string | null;
  /** Short AI-generated summary of this version's content. */
  description?: string | null;
  /**
   * The chat attachment this chain mirrors (#1144), or null/absent for a file
   * the assistant generated. Since `my-files` stopped listing a mirrored
   * upload separately, this chain is the file's only row — so the two fields
   * below travel with it, read off the ATTACHMENT row on the server.
   */
  attachment_id?: string | null;
  /** Honcho personal-memory ingest state (#536): none | ingested | failed. */
  memory_status?: string | null;
  /** full | no_text (#536). */
  indexing_quality?: string | null;
}

/**
 * The active share link on a file, as its OWNER sees it (#598).
 *
 * There is at most one per file — that is what makes it re-copyable instead of
 * minting a new link per copy.
 *
 * `token` is null when the link is valid but cannot be shown again, because no
 * encryption key is configured on the backend or it was rotated. The owner's
 * remedy is to revoke and create a new one; the existing link keeps working for
 * whoever already has it.
 */
export interface PersonalFileShareLink {
  id: string;
  created_at: string;
  token: string | null;
}

/**
 * Someone who has looked at the file through a link.
 *
 * Coarse on purpose — who, first, last. The backend upserts one row per person
 * per link rather than logging every render.
 */
export interface PersonalFileShareView {
  user_id: string;
  user_name: string | null;
  first_viewed_at: string;
  last_viewed_at: string;
}

/**
 * Everything the share dialog needs. `views` spans every link the file ever
 * had, so revoking one does not reset "who has seen my file".
 */
export interface PersonalFileShareState {
  link: PersonalFileShareLink | null;
  views: PersonalFileShareView[];
}

/** What a share link points at. Metadata only — no bytes, and no view recorded. */
export interface SharedFileInfo {
  filename: string;
  size_bytes: number;
  mime_type: string;
  shared_by_user_id: string;
  shared_by_name: string | null;
  /** The caller is the owner — the viewer says so instead of inventing a sharer. */
  is_own_file: boolean;
}

export interface PersonalFilesListResponse {
  items: PersonalFileListItem[];
  total: number;
}

/**
 * Config for embedding the interactive Collabora Online editor (Office files
 * only: docx/pptx/xlsx). The backend returns 415 for non-office MIME types and
 * 503 when Collabora / the WOPI signing secret is not configured.
 *
 * - `server` is the browser-facing Collabora origin (no path); the frontend
 *   derives the editor `urlsrc` from it via Collabora discovery.
 * - `wopi_src` is the absolute callback URL Collabora's SERVER hits (carrying
 *   only the WOPI token) — it points at the internal CAE FQDN, never the BFF.
 * - `access_token` is the short-lived owner+file-bound WOPI token; it MUST be
 *   sent in the POST body, never the query string (Referer leak).
 * - `access_token_ttl` is the absolute expiry (epoch ms) Collabora re-sends
 *   the token until.
 */
export interface CollaboraConfig {
  server: string;
  wopi_src: string;
  access_token: string;
  access_token_ttl: number;
}

/** Processing state of one Knowledge Area document made from a Drive file. */
export type BrainDocumentState = "queued" | "processing" | "completed" | "failed";

/** Summary state of a Drive file towards the Brain (cdb_tqs#1515). */
export type BrainFileState =
  | BrainDocumentState
  /** Video plan (TQS-4): an open block-upload session for this Drive file. */
  | "uploading"
  | "unsupported"
  | "not_synced"
  | "not_found";

export interface BrainDocumentStatus {
  document_id: string;
  knowledge_area: string;
  status: BrainDocumentState;
  /** Raw `tqs.documents.status`. */
  document_status: string;
  /** Why it failed; only while failed. */
  error_message: string | null;
  /** Drive version that was synced (null for chat attachments). */
  synced_version: number | null;
  updated_at: string | null;
  processed_at: string | null;
  /** Video plan §6.3: where a long video run is, when the backend says so. */
  media_progress?: { step: string; done?: number; total?: number } | null;
}

export interface BrainFileStatus {
  /** The id as requested. */
  file_id: string;
  found: boolean;
  chain_id: string | null;
  kind: "personal_file" | "attachment" | null;
  current_version: number | null;
  /** State of the most recently updated document, else unsupported/not_synced. */
  status: BrainFileState;
  /** The 415 body an upload would get, when `status` is `unsupported`. */
  unsupported: {
    code: string;
    message: string;
    extension: string | null;
    allowed: string[];
  } | null;
  /** Newest first. */
  documents: BrainDocumentStatus[];
}

export interface BrainStatusResponse {
  items: BrainFileStatus[];
  images_enabled: boolean;
}

/** Ids per `GET /personal-files/brain-status` request (server limit). */
export const BRAIN_STATUS_MAX_IDS = 100;

export const personalFilesApi = {
  /**
   * Knowledge Area processing state of the caller's Drive files
   * (`GET /personal-files/brain-status`, cdb_tqs#1515). At most
   * `BRAIN_STATUS_MAX_IDS` ids. Nothing is pushed — callers poll.
   */
  async getBrainStatus(ids: string[]): Promise<BrainStatusResponse> {
    const response = await fetchWithLogging(
      `${CDB_TQS_API_BASE_URL}/personal-files/brain-status`,
      { method: "GET", params: { ids: ids.join(",") } }
    );
    const raw = (response.data ?? {}) as Partial<BrainStatusResponse>;
    return {
      items: Array.isArray(raw.items) ? raw.items : [],
      images_enabled: raw.images_enabled === true,
    };
  },


  /**
   * List the user's personal files (latest-of-chain), newest first.
   *
   * Backed by `GET /personal-files`. The backend may return either an
   * `{ items, total }` envelope or a bare array — both are normalized here so
   * the Drive listing never depends on the exact server shape.
   */
  async listPersonalFiles(
    params: { limit?: number; offset?: number; search?: string } = {}
  ): Promise<PersonalFilesListResponse> {
    const queryParams: Record<string, string> = {};
    if (params.limit != null) queryParams.limit = String(params.limit);
    if (params.offset != null) queryParams.offset = String(params.offset);
    if (params.search) queryParams.search = params.search;

    const response = await fetchWithLogging(
      `${CDB_TQS_API_BASE_URL}/personal-files`,
      { method: "GET", params: queryParams }
    );

    const raw = response.data;
    if (Array.isArray(raw)) {
      const items = raw as PersonalFileListItem[];
      return { items, total: items.length };
    }
    const envelope = (raw ?? {}) as Partial<PersonalFilesListResponse>;
    const items = Array.isArray(envelope.items) ? envelope.items : [];
    return {
      items,
      total: typeof envelope.total === "number" ? envelope.total : items.length,
    };
  },

  /**
   * Download the latest version's raw bytes and create a blob URL.
   * Convenience wrapper over `downloadVersion` (no explicit version → latest).
   */
  async downloadPersonalFile(fileId: string, filename: string): Promise<string> {
    return this.downloadVersion(fileId, filename);
  },

  /**
   * Fetch the version chain for a file, newest first.
   *
   * @param fileId - Any file id in the chain (latest-of-chain in Drive 2.0)
   */
  async getVersions(fileId: string): Promise<PersonalFileVersion[]> {
    const response = await fetchWithLogging(
      `${CDB_TQS_API_BASE_URL}/personal-files/${fileId}/versions`,
      { method: "GET" }
    );
    const data = (response.data ?? []) as PersonalFileVersion[];
    // Newest first — the backend already orders this way, but sort defensively
    // so the UI never depends on server ordering.
    return [...data].sort(
      (a, b) => (b.version_number ?? 0) - (a.version_number ?? 0)
    );
  },

  /**
   * Restore an older version as a new latest version (non-destructive).
   * Returns the newly created version row.
   *
   * Both arguments are load-bearing (#529). The path segment is the CHAIN id,
   * not the id of the version row being restored — the route is
   * `/personal-files/{file_id}/restore` — and `?version=` is what selects which
   * one comes back. Sending the version-row id as `file_id` and omitting
   * `version` made the backend fall through to its default, which is the
   * *oldest* version of the chain. So restoring V2 quietly produced V1.
   *
   * Mirrors `downloadVersion` below, which addresses a version the same way.
   *
   * @param fileId - Chain file id (`PersonalFileListItem.id`), NOT a version id
   * @param version - The version number to restore
   */
  async restoreVersion(
    fileId: string,
    version: number
  ): Promise<PersonalFileVersion> {
    const response = await fetchWithLogging(
      `${CDB_TQS_API_BASE_URL}/personal-files/${fileId}/restore?version=${version}`,
      { method: "POST" }
    );
    return response.data as PersonalFileVersion;
  },

  /**
   * Download the raw bytes of a specific version and create a blob URL.
   * The caller is responsible for revoking the returned URL.
   *
   * @param fileId - Chain file id
   * @param filename - Original filename, used to set the correct MIME type
   * @param version - Optional version number (defaults to latest server-side)
   */
  async downloadVersion(
    fileId: string,
    filename: string,
    version?: number,
    signal?: AbortSignal
  ): Promise<string> {
    const params = version != null ? `?version=${version}` : "";
    const url = `${CDB_TQS_API_BASE_URL}/personal-files/${fileId}/download${params}`;

    logApiCall("GET", url);

    try {
      const authHeaders = await getAuthHeaders();
      const response = await fetch(url, {
        method: "GET",
        credentials: "include",
        headers: { ...authHeaders },
        signal,
      });

      if (!response.ok) {
        throw new Error(
          `Failed to download version: ${response.status} ${response.statusText}`
        );
      }

      logApiResponse("GET", url, response.status, { filename, version });

      const blob = await response.blob();
      const mimeType = getMimeTypeFromFilename(filename);
      const typedBlob =
        mimeType && blob.type !== mimeType
          ? new Blob([blob], { type: mimeType })
          : blob;

      return URL.createObjectURL(typedBlob);
    } catch (error) {
      logError(error as Error, {
        method: "GET",
        url,
        context: "Personal file version download failed",
      });
      throw error;
    }
  },

  /**
   * Render a version via the preview endpoint and create a blob URL. DOCX/PPTX
   * arrive Collabora-converted to PDF (see `preview_personal_file` in cdb_tqs);
   * PDF/image formats stream as-is. Unlike `downloadVersion`, this does NOT
   * relabel the blob to the original filename's MIME type — for converted
   * formats that would be actively wrong (the bytes are a PDF).
   *
   * @param fileId - Chain file id
   * @param filename - Original filename (only used for logging/error context)
   * @param version - Optional version number (defaults to latest server-side)
   */
  async previewVersion(
    fileId: string,
    filename: string,
    version?: number,
    signal?: AbortSignal
  ): Promise<string> {
    const params = version != null ? `?version=${version}` : "";
    const url = `${CDB_TQS_API_BASE_URL}/personal-files/${fileId}/preview${params}`;

    logApiCall("GET", url);

    try {
      const authHeaders = await getAuthHeaders();
      const response = await fetch(url, {
        method: "GET",
        credentials: "include",
        headers: { ...authHeaders },
        // A pinned version addresses immutable content and may come from the
        // cache. WITHOUT one this URL is a mutable alias for "latest": after an
        // edit the re-fetch produces the identical URL, and the browser used to
        // answer it from cache — the document kept rendering its previous
        // content while the server was already returning the new one (#553).
        // The server-side header is fixed in cdb_tqs too; this makes the client
        // correct on its own, and against any deploy order.
        cache: version != null ? "default" : "no-cache",
        signal,
      });

      if (!response.ok) {
        throw new Error(
          `Failed to load preview: ${response.status} ${response.statusText}`
        );
      }

      logApiResponse("GET", url, response.status, { filename, version });

      const blob = await response.blob();
      return URL.createObjectURL(blob);
    } catch (error) {
      logError(error as Error, {
        method: "GET",
        url,
        context: "Personal file preview failed",
      });
      throw error;
    }
  },

  /**
   * Download a specific version's raw bytes as an ArrayBuffer (no blob URL).
   *
   * The BPMN modeler and the HTML editor parse the raw bytes themselves.
   * Identical fetch to `downloadVersion` (user Bearer → backend owner-guard) but returns the
   * ArrayBuffer directly instead of building an object URL.
   *
   * @param fileId - Chain file id
   * @param filename - Original filename (kept for parity / logging context)
   * @param version - Optional version number (defaults to latest server-side)
   */
  async downloadVersionBytes(
    fileId: string,
    filename: string,
    version?: number,
    signal?: AbortSignal
  ): Promise<ArrayBuffer> {
    const params = version != null ? `?version=${version}` : "";
    const url = `${CDB_TQS_API_BASE_URL}/personal-files/${fileId}/download${params}`;

    logApiCall("GET", url);

    try {
      const authHeaders = await getAuthHeaders();
      const response = await fetch(url, {
        method: "GET",
        credentials: "include",
        headers: { ...authHeaders },
        // Same reason as downloadVersion (#553/#555): without a pinned version
        // this URL is a mutable alias for "latest", so a re-fetch after an edit
        // produces the identical URL and the browser would answer it from cache.
        // This path feeds the BPMN modeler and the HTML editor, both of
        // which re-read exactly after writing a new version.
        cache: version != null ? "default" : "no-cache",
        signal,
      });

      if (!response.ok) {
        throw new Error(
          `Failed to download version: ${response.status} ${response.statusText}`
        );
      }

      logApiResponse("GET", url, response.status, { filename, version });

      return await response.arrayBuffer();
    } catch (error) {
      logError(error as Error, {
        method: "GET",
        url,
        context: "Personal file version bytes download failed",
      });
      throw error;
    }
  },

  /**
   * Fetch the embed config for the interactive Collabora Online editor (Office
   * files only: docx/pptx/xlsx). The backend rejects non-office MIME types with
   * 415 and returns 503 when Collabora / WOPI signing is not configured — the
   * caller uses those statuses to hide the "Edit in Collabora" CTA.
   *
   * @param fileId - Chain file id (latest-of-chain in Drive 2.0)
   */
  async getCollaboraConfig(fileId: string): Promise<CollaboraConfig> {
    const response = await fetchWithLogging(
      `${CDB_TQS_API_BASE_URL}/personal-files/${fileId}/collabora-config`,
      { method: "GET" }
    );
    return response.data as CollaboraConfig;
  },

  /**
   * Save raw edited bytes as a new version of a file.
   *
   * Used by the in-app editors (BPMN modeler, HTML editor): the frontend POSTs
   * the edited bytes with the user Bearer. The backend owner-guard authorizes
   * the write, appends a new row at `max(version_number) + 1` and returns it.
   *
   * @param fileId - Chain file id
   * @param bytes - Raw file bytes
   * @param mimeType - Content-Type to send (e.g. `application/pdf`)
   */
  async saveNewVersion(
    fileId: string,
    bytes: ArrayBuffer | Blob,
    mimeType: string
  ): Promise<PersonalFileVersion> {
    const url = `${CDB_TQS_API_BASE_URL}/personal-files/${fileId}/versions`;

    logApiCall("POST", url);

    try {
      const authHeaders = await getAuthHeaders();
      const response = await fetch(url, {
        method: "POST",
        credentials: "include",
        headers: { ...authHeaders, "Content-Type": mimeType },
        body: bytes,
      });

      if (!response.ok) {
        let detail = `HTTP ${response.status}`;
        try {
          detail = (await response.json())?.detail || detail;
        } catch {
          /* non-JSON error body — keep the status fallback */
        }
        throw new Error(detail);
      }

      logApiResponse("POST", url, response.status, { mimeType });
      return (await response.json()) as PersonalFileVersion;
    } catch (error) {
      logError(error as Error, {
        method: "POST",
        url,
        context: "Personal file new-version save failed",
      });
      throw error;
    }
  },

  /**
   * Rename a personal file's whole version chain (`PATCH
   * /personal-files/{file_id}`).
   *
   * The whole chain, not the version: every version shares one logical name,
   * so renaming a single row would show the same file under two names in its
   * own history. The extension is kept server-side whatever is sent — it
   * decides how the file is previewed, converted and edited (cdb_webos#612).
   */
  async renamePersonalFile(
    fileId: string,
    filename: string,
  ): Promise<PersonalFileListItem> {
    const { data } = await fetchWithLogging(
      `${CDB_TQS_API_BASE_URL}/personal-files/${fileId}`,
      { method: "PATCH", body: JSON.stringify({ filename }) },
    );
    return data as PersonalFileListItem;
  },

  /**
   * Soft-delete a personal file's whole version chain (`DELETE
   * /personal-files/{file_id}`) — idempotent, owner-scoped server-side.
   * Blobs stay in storage for a later retention/GC job; the chain just
   * stops showing up in listings.
   */
  async deletePersonalFile(fileId: string): Promise<void> {
    await fetchWithLogging(`${CDB_TQS_API_BASE_URL}/personal-files/${fileId}`, {
      method: "DELETE",
    });
  },

  // ===== Share links (#598) =====
  //
  // The token is the authorization here, unlike the corporate document links of
  // #516 where the link is only an address. A link grants SIGHT of the current
  // version — nothing is ever copied into the recipient's Drive. See the cdb_tqs
  // module docstring in `personal_file_shares.py`.
  //
  // `fileId` is whatever identifies the row in the Drive listing: the
  // personal-files chain id when there is one, otherwise the attachment id. The
  // backend probes both stores and records which it found, so the caller never
  // has to know — and therefore cannot get it wrong.

  /**
   * Get the file's share link, creating one only if it has none.
   *
   * Deliberately not "create": a second call must hand back the SAME link
   * rather than invalidate the one already sitting in somebody's mailbox.
   */
  async createShareLink(fileId: string): Promise<PersonalFileShareState> {
    const response = await fetchWithLogging(
      `${CDB_TQS_API_BASE_URL}/personal-files/${fileId}/share-links`,
      { method: "POST" }
    );
    return response.data as PersonalFileShareState;
  },

  /** Read the current state without creating anything. */
  async getShareState(fileId: string): Promise<PersonalFileShareState> {
    const response = await fetchWithLogging(
      `${CDB_TQS_API_BASE_URL}/personal-files/${fileId}/share-links`,
      { method: "GET" }
    );
    return (response.data ?? { link: null, views: [] }) as PersonalFileShareState;
  },

  /**
   * Render an HTML page to PDF and file it in the Drive (#1214).
   *
   * The PDF gets its own version chain named after the page, so a second
   * export makes v2 rather than a second file. Always converts the page's
   * CURRENT version — the version switcher is a reading aid, not a selection.
   *
   * `warnings` names constructs whose content is missing from the PDF: no
   * converter runs JavaScript, so a page that draws a chart on a `<canvas>` or
   * builds rows by script produces a PDF that looks complete and is not. Show
   * them; a silently gutted document is the failure mode worth naming.
   *
   * Throws `CdbTqsApiError` with status 503 where the deployment has no
   * renderer, 413 for a page over the size ceiling, 415 for anything that is
   * not an HTML page, and 502 when the renderer refused or timed out.
   */
  async exportPdf(fileId: string): Promise<PersonalFilePdfExport> {
    const response = await fetchWithLogging(
      `${CDB_TQS_API_BASE_URL}/personal-files/${fileId}/export/pdf`,
      { method: "POST" }
    );
    return response.data as PersonalFilePdfExport;
  },

  /** Close a link. Takes effect at once, including for viewers already open. */
  async revokeShareLink(linkId: string): Promise<void> {
    await fetchWithLogging(
      `${CDB_TQS_API_BASE_URL}/personal-files/share-links/${linkId}`,
      { method: "DELETE" }
    );
  },

  /** Resolve a share link to its metadata. No bytes, and not counted as a view. */
  async getSharedFileInfo(token: string): Promise<SharedFileInfo> {
    const response = await fetchWithLogging(
      `${CDB_TQS_API_BASE_URL}/personal-files/shared/${encodeURIComponent(token)}`,
      { method: "GET" }
    );
    return response.data as SharedFileInfo;
  },

  /**
   * Fetch the shared file for display and create a blob URL.
   *
   * The SERVER chooses the representation — Office converted to PDF, everything
   * else as stored — so there is deliberately no format logic here. The blob is
   * therefore NOT relabelled to the original filename's MIME type: for a
   * converted document that would be wrong.
   *
   * `no-cache` because this URL is a mutable alias for "latest". Caching it
   * would keep showing the pre-edit document after the owner saves — and would
   * keep serving a file whose link has since been revoked.
   *
   * Throws `CdbTqsApiError` so the page can tell "link is dead" (404) from
   * "backend is down", which decides which of two very different messages the
   * recipient sees.
   */
  async getSharedFileContent(token: string, signal?: AbortSignal): Promise<string> {
    const url = `${CDB_TQS_API_BASE_URL}/personal-files/shared/${encodeURIComponent(
      token
    )}/content`;

    logApiCall("GET", url);

    try {
      const authHeaders = await getAuthHeaders();
      const response = await fetch(url, {
        method: "GET",
        credentials: "include",
        headers: { ...authHeaders },
        cache: "no-cache",
        signal,
      });

      if (!response.ok) {
        throw new CdbTqsApiError(
          response.status,
          `Failed to load shared file: ${response.status} ${response.statusText}`
        );
      }

      logApiResponse("GET", url, response.status, {});

      const blob = await response.blob();
      return URL.createObjectURL(blob);
    } catch (error) {
      logError(error as Error, {
        method: "GET",
        url,
        context: "Shared file content failed",
      });
      throw error;
    }
  },
};
